import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { IMyDateModel } from '@nodro7/angular-mydatepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SPP_OVERLAY_POSITION_DROPDOWN } from '../common-ui-common.config';
import { LanguageService } from '../i18n/language.service';
import { I18N } from './date-picker.component.i18n';
import { DatePickerConfig } from './date-picker.config';
import { DatePickerUtil } from './date-picker.util';


const VALIDATE_PARSE_ERROR = {
    dateParseError: {
        valid: false
    }
};

/**
 * Diese Komponente ist ein Datepicker Wrapper für ngx-mydatepicker {@link https://github.com/kekeh/ngx-mydatepicker/}
 *
 * Konfigurierbar durch date-picker.config.ts
 *
 * @deprecated Bessere Alternative umsetzen (z.B. Angular Material Datepicker)
 */
@Component({
    selector: 'common-date-picker',
    template: `
        <div class="commonDatepicker" [ngClass]="{commonDatepickerOpened: isOpen}">
            <input
                cdkOverlayOrigin
                #inputElement
                #origin="cdkOverlayOrigin"
                ngDefaultControl
                [ngModel]="model | date:options.displayDateFormat"
                (ngModelChange)="onInputChange($event)"
                [ngModelOptions]="{ updateOn: 'blur' }"
                placeholder="{{placeholder}}"
                class="inputField commonDatepickerInput"
                [attr.disabled]="disabled ? '' : null"
                (click)="open()"/>
            <span class="icon-calendar" *ngIf="calendarIcon && !clearButton"
                  (click)="open()"></span>
            <span class="icon-shut" *ngIf="clearButton && model !== undefined"
                  (click)="!disabled && clear();$event.stopPropagation()"></span>
        </div>
        <ng-template
            cdk-connected-overlay
            cdkConnectedOverlayHasBackdrop
            cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
            [cdkConnectedOverlayOrigin]="origin"
            [cdkConnectedOverlayPositions]="overlayPosition"
            [cdkConnectedOverlayOpen]="isOpen"
            [cdkConnectedOverlayFlexibleDimensions]="true"
            [cdkConnectedOverlayMinWidth]="280"
            (backdropClick)="isOpen = false"
            (detach)="onDetach()"
        >
            <common-date-picker-overlay
                [options]="options"
                [model]="model"
                (close)="isOpen = false"
                (touch)="touch$.next()"
                (dateChange)="onDateChange($event)"
            ></common-date-picker-overlay>
        </ng-template>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        }
    ]
})
export class DatePickerComponent implements ControlValueAccessor, OnInit, OnChanges, Validator, OnDestroy {

    @Input() options: DatePickerConfig;

    @Input() clearButton: boolean = true;

    @Input() calendarIcon: boolean = false;

    @Input() disabled: boolean = false;

    @ViewChild('inputElement') inputElement: ElementRef;

    isOpen = false;

    placeholder: string;

    touch$ = new Subject<void>();

    model: Date;

    overlayPosition = SPP_OVERLAY_POSITION_DROPDOWN;

    private ngUnsubscribe$ = new Subject<void>();

    private isValid: boolean = true;

    private validityChange$: EventEmitter<any> = new EventEmitter<any>();

    // NgxMyDatePickerDirective kann nur ein Callback ersetzen.
    private valueChange$: EventEmitter<any> = new EventEmitter<any>();


    constructor(
        private languageService: LanguageService,
        private cd: ChangeDetectorRef
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('options')) {
            this.options = this.prepareOptions();
        }
    }

    ngOnInit() {
        if (this.languageService.currentLanguage && I18N[this.languageService.currentLanguage]) {
            this.placeholder = I18N[this.languageService.currentLanguage].placeholder;
        }
        this.options = this.prepareOptions();
    }

    ngOnDestroy() {
        this.validityChange$.complete();
        this.valueChange$.complete();
    }

    writeValue(value: Date | string | number) {
        if (value === undefined || value === null) {
            this.changeValidity(true);
            this.model = undefined;
            this.cd.detectChanges();
            return;
        }
        // Das Datum-Objekt kann Date, Iso String oder Int sein.
        // Date Format (dd.mm.jjjj) wird lediglich innerhalb Datepicker benutzt.
        const parsedDate = new Date(value);
        if (isNaN(parsedDate.getTime()) || !(parsedDate instanceof Date)) {
            this.changeValidity(false);
            return;
        }
        this.model = parsedDate;
        this.cd.detectChanges();
    }

    validate(_: AbstractControl) {
        return this.isValid ? undefined : VALIDATE_PARSE_ERROR;
    }

    clear() {
        this.writeValue(undefined);
        this.valueChange$.next(undefined);
    }

    open() {
        this.isOpen = true;
    }

    registerOnValidatorChange(fn: () => void) {
        this.validityChange$.subscribe(fn);
    }

    registerOnChange(fn: (x: Date) => void) {
        this.valueChange$.subscribe((x: Date) => fn(x));
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    registerOnTouched(fn: any): void {
        this.touch$.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(() => fn());
    }

    @HostListener('keydown.arrowDown', ['$event'])
    onArrowDown(event: KeyboardEvent): void {
        event.preventDefault();
        this.open();
    }

    onInputChange(date: string) {
        const options = this.prepareOptions();
        const validity = DatePickerUtil.isDateValid(date, options);
        this.changeValidity(validity);
        if (!validity) {
            return;
        }
        this.model = DatePickerUtil.parseDate(date, options);
        this.valueChange$.next(this.model);
    }

    onDateChange(dateModel: IMyDateModel) {
        this.writeValue(dateModel.singleDate.jsDate);
        this.changeValidity(true);
        this.valueChange$.next(dateModel.singleDate.jsDate);
    }

    onDetach() {
        this.isOpen = false;
        this.inputElement.nativeElement.focus();
    }

    private prepareOptions(): DatePickerConfig {
        if (this.languageService.currentLanguage && I18N[this.languageService.currentLanguage]) {
            return Object.assign(new DatePickerConfig(), this.options || {}, I18N[this.languageService.currentLanguage]);
        }

        return Object.assign(new DatePickerConfig(), this.options || {});
    }

    private changeValidity(isValid: boolean) {
        if (isValid === this.isValid) {
            return;
        }
        this.isValid = isValid;
        this.validityChange$.emit(isValid ? undefined : VALIDATE_PARSE_ERROR);
    }
}
